import { Action, Subject } from '@erp/authz';
import { Link } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { CashReceiptDetail, TaskResponse } from '@erp/rpc';
import {
  useArCashReceiptWorkItem,
  useCashReceiptLatestLogTask,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import { useTaskById } from '@/api/rpc/hooks/tasks/useTaskDetail';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import { useListAgentDefinitions } from '@/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useCan } from '@/hooks/useCan';
import { Badge } from '@/components/ui/badge';
import { TaskHelpRequestCard } from '@/components/tasks/TaskHelpRequestCard';
import {
  TaskDisplayIdChip,
  TaskPriorityLabel,
  TaskStatusSelect,
  TaskSummaryBlocks,
} from '@/components/tasks/TaskDetailBlocks';
import {
  TaskAssigneeControl,
  TaskCloseControls,
} from '@/pages/authenticated/tasks/$taskId/TaskReviewControls';
import { CreateLogTaskDialog } from './CreateLogTaskDialog';
import {
  formatLogOutcome,
  formatLogTaskAssignee,
  formatLogTaskStatus,
  logTaskStatusClass,
} from './cashReceiptTaskFormat';
import {
  readTaskSummaries,
  resolveCashReceiptTaskCardState,
} from './cashReceiptTaskCard';

type NamedRow = { id: string; name: string };

function SectionHeading({ taskId }: { taskId: string | null }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="AR cash receipt, section heading for the receipt's logging task">
          Task
        </T>
      </p>
      {taskId && <OpenTaskLink taskId={taskId} />}
    </div>
  );
}

function OpenTaskLink({ taskId }: { taskId: string }) {
  return (
    <Link
      to="/tasks/$taskId"
      params={{ taskId }}
      viewTransition
      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
    >
      <T context="AR cash receipt task card, link to the full task page">
        Open task
      </T>
      <ArrowSquareOutIcon className="size-3" />
    </Link>
  );
}

/**
 * Who and what the agents and users are, for the assignee picker and the
 * help request's "<agent> needs your input" heading.
 */
function useTaskPeople(): { users: NamedRow[]; agents: NamedRow[] } {
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);
  const { data: usersData } = useUsersInOrg();
  const { data: agentData } = useListAgentDefinitions({
    limit: 50,
    enabled: canReadAgents,
  });
  return {
    users: (usersData ?? []).map((row) => ({ id: row.id, name: row.name })),
    agents: (agentData?.definitions ?? []).map((row) => ({
      id: row.id,
      name: row.name,
    })),
  };
}

function OpenTaskBody({
  task,
  displayId,
  users,
  agents,
}: {
  task: TaskResponse | null;
  displayId: string | null;
  users: NamedRow[];
  agents: NamedRow[];
}) {
  if (!task) {
    // The viewer may not read tasks (or it is still loading): the id is all
    // the work item carries.
    return displayId ? (
      <div className="text-xs">
        <TaskDisplayIdChip displayId={displayId} />
      </div>
    ) : null;
  }
  const { summary, agentSummary } = readTaskSummaries(task.metadata);
  const agentNameById = new Map(agents.map((agent) => [agent.id, agent.name]));
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-stone-800">
        {task.displayId && <TaskDisplayIdChip displayId={task.displayId} />}
        <TaskStatusSelect taskId={task.id} status={task.status} />
        <TaskPriorityLabel priority={task.priority} />
      </div>
      {/* The agent's open question + reply form, while waiting on a human. */}
      <TaskHelpRequestCard task={task} agentNameById={agentNameById} />
      <TaskSummaryBlocks summary={summary} agentSummary={agentSummary} />
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="min-w-[12rem] flex-1">
          <TaskAssigneeControl task={task} users={users} agents={agents} />
        </div>
        <TaskCloseControls task={task} />
      </div>
    </>
  );
}

function ClosedTaskLine({
  task,
  users,
  agents,
}: {
  task: TaskResponse;
  users: NamedRow[];
  agents: NamedRow[];
}) {
  const gt = useGT();
  const assigneeName =
    agents.find((agent) => agent.id === task.assigneeAgentDefinitionId)?.name ??
    users.find((user) => user.id === task.assigneeUserId)?.name ??
    null;
  const closedAt = task.completedAt ?? task.updatedAt;
  return (
    <div
      className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs"
      data-testid="cash-receipt-task-closed"
    >
      <Badge
        variant="outline"
        className={`shrink-0 text-xs ${logTaskStatusClass(task.status)}`}
      >
        {task.outcome
          ? formatLogOutcome(task.outcome, gt)
          : formatLogTaskStatus(task.status, gt)}
      </Badge>
      {task.outcomeReason && (
        <span
          className="min-w-0 truncate text-stone-700"
          title={task.outcomeReason}
        >
          {task.outcomeReason}
        </span>
      )}
      <span className="text-[11px] text-muted-foreground">
        {task.displayId && `${task.displayId} · `}
        <T context="AR cash receipt task card: when the logging task closed and who had it">
          closed <Var>{formatDistanceToNow(closedAt, { addSuffix: true })}</Var>{' '}
          · <Var>{formatLogTaskAssignee(task, assigneeName, gt)}</Var>
        </T>
      </span>
    </div>
  );
}

/**
 * The receipt's log-cash-receipt task, first in the detail view: the open
 * task's state, the agent's question with its answer box, its summaries,
 * assignee and close controls; else one line on how the last task closed;
 * else the button that files one. Comments, the recording and the run
 * timeline stay on the task page, one link away.
 */
export function CashReceiptTaskSection({
  receipt,
}: {
  receipt: CashReceiptDetail;
}) {
  const { workItem, isLoading: workItemLoading } = useArCashReceiptWorkItem(
    receipt.id,
  );
  const openTask = workItem?.task ?? null;
  const { data: openTaskDetail } = useTaskById(openTask?.id ?? null);
  const { task: latestTask, isLoading: latestLoading } =
    useCashReceiptLatestLogTask(receipt.id);
  const { users, agents } = useTaskPeople();

  const state = resolveCashReceiptTaskCardState({
    isLoading: workItemLoading || latestLoading,
    openTask,
    openTaskDetail,
    latestTask,
  });
  if (state.kind === 'loading') return null;

  if (state.kind === 'none') {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <SectionHeading taskId={null} />
          <p className="text-xs text-muted-foreground">
            <T context="AR cash receipt task card when the receipt has never had a logging task">
              No logging task for this receipt.
            </T>
          </p>
        </div>
        <CreateLogTaskDialog receipt={receipt} />
      </div>
    );
  }

  if (state.kind === 'closed') {
    return (
      <div className="space-y-2">
        <SectionHeading taskId={state.task.id} />
        <ClosedTaskLine task={state.task} users={users} agents={agents} />
      </div>
    );
  }

  return (
    <div
      className="space-y-3"
      data-testid="cash-receipt-task-open"
      data-state={state.needsInput ? 'needs-input' : 'open'}
    >
      <SectionHeading taskId={state.taskId} />
      <OpenTaskBody
        task={state.task}
        displayId={state.displayId}
        users={users}
        agents={agents}
      />
    </div>
  );
}
